import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = 'tracker-table-widths'

/**
 * Starting widths, in pixels. Chosen so the table reads left to right without any column having to
 * stretch: leftover space becomes an empty track on the right instead of inflating the title
 * (TableView builds the grid that way).
 */
export const DEFAULT_WIDTHS: Readonly<Record<string, number>> = {
  ref: 62,
  title: 420,
  status: 128,
  priority: 58,
  assignee: 132,
  createdAt: 92,
  dueDate: 84,
  labels: 160,
  comments: 48,
}

/** Below this a column stops being a column. Dragging clamps here, so nothing can be lost. */
export const MIN_WIDTH = 44
const MIN_TITLE_WIDTH = 140
const MAX_WIDTH = 900

export function minWidthFor(columnId: string): number {
  return columnId === 'title' ? MIN_TITLE_WIDTH : MIN_WIDTH
}

function readStored(): Record<string, number> {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return {}

    // Only numbers, only sane ones. A stored width for a column that no longer exists is ignored,
    // and a column added later simply has no entry and falls back to its default — so shipping a
    // new column never leaves anybody with a broken layout they have to clear by hand.
    const clean: Record<string, number> = {}
    for (const [key, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof value === 'number' && Number.isFinite(value)) {
        clean[key] = Math.min(MAX_WIDTH, Math.max(minWidthFor(key), value))
      }
    }
    return clean
  } catch {
    // Corrupted entry from an older version — defaults are a better answer than a crash.
    return {}
  }
}

/**
 * Column widths the person set for themselves, remembered between visits.
 *
 * localStorage rather than the database: this is a per-screen view preference, like the theme
 * (ui-spec §5.5). Storing it server-side would mean a table, an endpoint and cross-device sync for
 * a setting that has to be readjusted on a different monitor anyway. The cost is that another
 * laptop starts from the defaults.
 */
export function useColumnWidths(): {
  widths: Record<string, number>
  setWidth: (columnId: string, px: number) => void
  resetWidth: (columnId: string) => void
} {
  const [stored, setStored] = useState<Record<string, number>>(readStored)

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
    } catch {
      // Private mode or a full quota. Widths for this session still work; they just do not persist.
    }
  }, [stored])

  const setWidth = useCallback((columnId: string, px: number) => {
    const clamped = Math.min(MAX_WIDTH, Math.max(minWidthFor(columnId), Math.round(px)))
    setStored((previous) => ({ ...previous, [columnId]: clamped }))
  }, [])

  const resetWidth = useCallback((columnId: string) => {
    setStored((previous) => {
      const next = { ...previous }
      delete next[columnId]
      return next
    })
  }, [])

  return { widths: { ...DEFAULT_WIDTHS, ...stored }, setWidth, resetWidth }
}
